import { questionTextToPlainText } from 'utils/questionMarkdown';
import React, { useCallback, useContext, useEffect, useMemo, useState, useRef } from 'react';
import { AnswerReferenceContext } from 'utils/answerReferences';
import type { Question, ResponsePayload } from '../../../types';
import { sanitizeTextList } from 'utils/responseNormalization';
import { TextList } from '../../questions/TextList';
import { registerQuestionRenderer, type QuestionRendererComponent } from './registry';

const computeDefaultTextList = (question: Question): string[] => {
  const metadata = (question?.metadata ?? {}) as Record<string, unknown>;

  const arrayCandidates = [
    metadata['text_list'],
    metadata['text_list_defaults'],
  ];

  for (const candidate of arrayCandidates) {
    const list = sanitizeTextList(candidate);
    if (list.length) {
      return list;
    }
  }

  const lengthCandidates = [
    metadata['text_list_length'],
    metadata['text_list_count'],
    metadata['list_length'],
  ];

  for (const candidate of lengthCandidates) {
    const numeric = typeof candidate === 'number' ? candidate : Number(candidate);
    if (Number.isFinite(numeric) && numeric > 0) {
      return Array.from({ length: numeric }, () => '');
    }
  }

  const options = Array.isArray(question?.options) ? question.options : [];
  if (options.length > 0) {
    const optionValues = options.map((option) =>
      typeof option?.front_text === 'string' ? option.front_text : '',
    );
    if (optionValues.some((value) => value.trim().length > 0)) {
      return sanitizeTextList(optionValues);
    }
    return Array.from({ length: options.length }, () => '');
  }

  return ['', '', ''];
};

const resolveTextListLabels = (question: Question, rowCount: number): string[] => {
  const metadata = (question?.metadata ?? {}) as Record<string, unknown>;
  const candidate = metadata['text_list_labels'];

  if (!Array.isArray(candidate)) {
    return Array.from({ length: rowCount }, () => '');
  }

  return Array.from({ length: rowCount }, (_, index) => {
    const label = candidate[index];
    return typeof label === 'string' ? label.trim() : '';
  });
};

/**
 * A default ending in a colon ("Thought:") is a worksheet label, not an answer. It is shown as the
 * line's label, and the saved line keeps today's "Thought: answer" format for summaries and exports.
 */
const resolveLabelPrefixes = (question: Question): string[] =>
  computeDefaultTextList(question).map((entry) =>
    /:\s*$/.test(entry) ? entry.replace(/\s*:\s*$/, '').trim() : '',
  );

const escapeRegExp = (value: string) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const stripLabelPrefix = (value: string, prefix: string) =>
  prefix ? value.replace(new RegExp(`^\\s*${escapeRegExp(prefix)}\\s*:?\\s*`, 'i'), '') : value;

const withLabelPrefixes = (values: string[], prefixes: string[]) =>
  values.map((value, index) => (value && prefixes[index] ? `${prefixes[index]}: ${value}` : value));

/** The learner's own answers, with any worksheet labels taken out. */
const resolveInitialAnswers = (question: Question, existing: unknown): string[] => {
  const prefixes = resolveLabelPrefixes(question);
  const fromExisting = sanitizeTextList(existing);
  const source = fromExisting.length > 0 ? fromExisting : computeDefaultTextList(question);
  return source.map((value, index) => stripLabelPrefix(value, prefixes[index] ?? '').trim());
};

const TextListRenderer: QuestionRendererComponent = ({
  question,
  existingResponse,
  isSubmitting,
  onDraftChange,
  onHasUserInputChange,
  onValidityChange,
  onExtrasChange,
}) => {
  void onExtrasChange;
  const answerReferences = useContext(AnswerReferenceContext);
  const questionId = question?.id ?? existingResponse?.question ?? -1;
  const prevQuestionIdRef = useRef(questionId);

  const labelPrefixes = useMemo(() => resolveLabelPrefixes(question), [question]);
  const [values, setValues] = useState<string[]>(() =>
    resolveInitialAnswers(question, existingResponse?.text_list),
  );

  const emit = useCallback(
    (nextValues: string[]) => {
      const sanitized = sanitizeTextList(nextValues);
      const hasAnyValue =
        sanitized.length > 0 && sanitized.some((entry) => entry.length > 0);
      const hasAllValues =
        sanitized.length > 0 && sanitized.every((entry) => entry.length > 0);

      onHasUserInputChange?.(hasAnyValue);
      onValidityChange?.(hasAllValues);

      if (!hasAnyValue) {
        onDraftChange(null);
        return;
      }

      const saved = withLabelPrefixes(sanitized, labelPrefixes);
      const payload: ResponsePayload = {
        question: questionId,
        text_list: saved,
        text_value: saved.join('\n'),
      };

      onDraftChange(payload);
    },
    [labelPrefixes, onDraftChange, onHasUserInputChange, onValidityChange, questionId],
  );

  // Only reset when navigating to a different question
  useEffect(() => {
    if (prevQuestionIdRef.current !== questionId) {
      const initialList = resolveInitialAnswers(question, existingResponse?.text_list);

      setValues(initialList);
      emit(initialList);
      prevQuestionIdRef.current = questionId;
    }
  }, [questionId, existingResponse?.text_list, emit, question]);

  const handleChange = (nextValues: string[]) => {
    setValues(nextValues);
    emit(nextValues);
  };

  return (
    <TextList
      disabled={isSubmitting}
      textList={values}
      setTextList={handleChange}
      labels={resolveTextListLabels(question, values.length).map(
        (label, index) => label || labelPrefixes[index] || '',
      )}
      groupLabel={questionTextToPlainText(question.text, { answerReferences })}
    />
  );
};

registerQuestionRenderer(['text_list'], TextListRenderer);
